// @ts-nocheck
import { useCallback, useEffect, useMemo, useState } from 'react';
import { NJBadge, NJFormItem, NJIconButton, NJMenu, NJMultiSelect, NJText, NJToastService, NJTooltip } from '@engie-group/fluid-design-system-react';
import { AgGridReact } from 'ag-grid-react';
import type { ColDef } from 'ag-grid-community';
import DataTable from '@shared/DataTable';
import type { DSP, ExplorerData, Network, Scenario, Selection, Study, StudyType } from './types';

const TYPE_LABEL: Record<string, string> = { 'hot water': 'Eau chaude', 'cold water': 'Eau froide', steam: 'Vapeur', condensate: 'Condensats' };
const TYPE_COLOR: Record<string, string> = { 'hot water': '#F34E46', 'cold water': '#007BC5', steam: '#e65100', condensate: '#6a1b9a' };
const TYPE_SHORT: Record<string, string> = { 'hot water': 'CH', 'cold water': 'FR', steam: 'VA', condensate: 'CO' };
const DSP_STATUS_LABEL: Record<string, string> = { active: 'Actif', archived: 'Archiv\u00e9' };
const STUDY_TYPE_VARIANT: Record<StudyType, 'information' | 'warning' | 'error'> = { D\u00e9veloppement: 'information', Conception: 'warning', Performance: 'error' };
const STUDY_TYPE_COLOR: Record<string, string> = { D\u00e9veloppement: '#0052CC', Conception: '#e65100', Performance: '#c62828' };
const SC_STATUS_LABEL: Record<string, string> = { active: 'Actif', archived: 'Archiv\u00e9', reference: 'R\u00e9f\u00e9rence' };
const SC_STATUS_VARIANT = { active: 'information', archived: 'unknown', reference: 'success' } as const;
const SC_ICON: Record<string, string> = { active: 'science', archived: 'inventory_2', reference: 'push_pin' };
const SC_COLOR: Record<string, string> = { active: '#0052CC', archived: '#9e9e9e', reference: '#2e7d32' };
const STATUS_FILTER_OPTIONS = ['active', 'archived', 'reference', 'orphan'] as const;

function DspTypeCell({ type }: { type: string }) {
  const fill = TYPE_COLOR[type] ?? '#9e9e9e';
  const id = `dlv-${fill.replace('#', '')}`;
  if (type === 'hot water' || type === 'cold water') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, height: '100%' }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
          <g clipPath={`url(#${id})`}>
            <path d="M17.7143 2H6.28571C5.02857 2 4.01143 3 4.01143 4.22222L4 22L12 18.6667L20 22V4.22222C20 3 18.9714 2 17.7143 2Z" fill={fill} />
            <path d="M14.9844 11.6357C14.9843 11.6337 14.9841 11.6318 14.9839 11.6299C14.9083 10.8171 14.4723 8.52986 11.9999 6C9.52643 8.53123 9.09116 10.8194 9.01576 11.6311L9.01551 11.6341C8.99751 11.8292 9.00007 11.9385 9.00019 11.9436V11.9448C9.00019 13.6321 10.3432 15 12 15C13.6568 15 15 13.6321 15 11.9448L14.9998 11.9424C15 11.9323 15.0019 11.8249 14.9844 11.6357Z" fill="white" />
          </g>
          <defs><clipPath id={id}><rect width="24" height="24" fill="white" /></clipPath></defs>
        </svg>
        <span style={{ fontSize: 11, color: fill, fontWeight: 700 }}>{TYPE_SHORT[type]}</span>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5, height: '100%' }}>
      <span className="material-symbols-outlined" style={{ fontSize: 16, color: fill }}>hub</span>
      <span style={{ fontSize: 11, color: fill, fontWeight: 700 }}>{TYPE_SHORT[type] ?? '?'}</span>
    </div>
  );
}

function DrillBtn({ label, onClick }: { label: string; onClick: (e: React.MouseEvent) => void }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
      <button
        onClick={onClick}
        style={{ fontSize: 12, fontWeight: 600, color: 'var(--nj-semantic-color-text-brand-primary-default)', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px', borderRadius: 4, display: 'inline-flex', alignItems: 'center', gap: 4, fontFamily: 'inherit' }}
        onMouseEnter={e => (e.currentTarget.style.background = 'var(--nj-semantic-color-background-brand-secondary-default)')}
        onMouseLeave={e => (e.currentTarget.style.background = 'none')}
      >
        {label}
        <span className="material-symbols-outlined" style={{ fontSize: 14 }}>chevron_right</span>
      </button>
    </div>
  );
}

function Breadcrumb({ items, onClickIdx }: { items: string[]; onClickIdx: (i: number) => void }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, flexShrink: 0 }}>
      {items.map((item, i) => (
        <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          {i > 0 && <span className="material-symbols-outlined" style={{ fontSize: 14, color: 'var(--nj-semantic-color-text-tertiary-default)' }}>chevron_right</span>}
          {i < items.length - 1 ? (
            <button onClick={() => onClickIdx(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--nj-semantic-color-text-brand-primary-default)', fontSize: 13, fontFamily: 'inherit' }}>
              {item}
            </button>
          ) : (
            <span style={{ fontWeight: 600, color: 'var(--nj-semantic-color-text-primary-default)' }}>{item}</span>
          )}
        </span>
      ))}
    </div>
  );
}

interface KebabMenuCellProps {
  dsp: DSP;
  onClone: (dsp: DSP) => void;
  onOpenSection: (dsp: DSP, section: string) => void;
  onDelete: (dsp: DSP) => void;
}

function KebabMenuCell({ dsp, onClone, onOpenSection, onDelete }: KebabMenuCellProps) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
      <NJMenu.Root>
        <NJMenu.Anchor>
          <NJIconButton icon="more_vert" label="Actions" variant="tertiary" scale="sm" />
        </NJMenu.Anchor>
        <NJMenu.Dropdown>
          <NJMenu.Group>
            <NJMenu.Item
              label="Cloner"
              leading={<span className="material-icons" style={{ fontSize: 16 }}>content_copy</span>}
              onClick={() => onClone(dsp)}
            />
          </NJMenu.Group>
          <NJMenu.Group>
            <NJMenu.Item
              label="Général"
              leading={<span className="material-icons" style={{ fontSize: 16 }}>info</span>}
              onClick={() => onOpenSection(dsp, 'Général')}
            />
            <NJMenu.Item
              label="Topologie"
              leading={<span className="material-icons" style={{ fontSize: 16 }}>account_tree</span>}
              onClick={() => onOpenSection(dsp, 'Topologie')}
            />
            <NJMenu.Item
              label="Production"
              leading={<span className="material-icons" style={{ fontSize: 16 }}>factory</span>}
              onClick={() => onOpenSection(dsp, 'Production')}
            />
            <NJMenu.Item
              label="Optimisations"
              leading={<span className="material-icons" style={{ fontSize: 16 }}>stacked_bar_chart</span>}
              onClick={() => onOpenSection(dsp, 'Optimisations')}
            />
          </NJMenu.Group>
          <NJMenu.Group>
            <NJMenu.Item
              label="Supprimer"
              leading={<span className="material-icons" style={{ fontSize: 16, color: 'var(--nj-semantic-color-text-danger-default)' }}>delete</span>}
              style={{ color: 'var(--nj-semantic-color-text-danger-default)' }}
              onClick={() => onDelete(dsp)}
            />
          </NJMenu.Group>
        </NJMenu.Dropdown>
      </NJMenu.Root>
    </div>
  );
}


function DspGroupRenderer({ data, onToggle }: { data?: DspFlatRow; onToggle?: (dspId: string) => void }) {
  if (!data || data._kind !== 'group') return null;
  const row = data;
  const typeColor = TYPE_COLOR[row._dsp.type] ?? '#9e9e9e';
  const summary = `${row.studiesCount} étude${row.studiesCount > 1 ? 's' : ''} • ${row.scenariosCount} réseau${row.scenariosCount > 1 ? 'x' : ''} • ${row.referenceCount} référence`;
  return (
    <div
      onClick={() => onToggle?.(row._dsp.id)}
      style={{ display: 'flex', alignItems: 'center', height: '100%', background: 'var(--nj-semantic-color-background-neutral-tertiary-default)', borderTop: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)', borderBottom: '1px solid var(--nj-semantic-color-border-neutral-secondary-default)', cursor: 'pointer' }}
      onMouseEnter={e => (e.currentTarget.style.background = 'var(--nj-semantic-color-background-neutral-secondary-default)')}
      onMouseLeave={e => (e.currentTarget.style.background = 'var(--nj-semantic-color-background-neutral-tertiary-default)')}
    >
      {/* Left accent bar in DSP type colour */}
      <div style={{ width: 7, height: '100%', background: typeColor, flexShrink: 0 }} />
      {/* Accordion chevron */}
      <span
        className="material-symbols-outlined"
        style={{ fontSize: 18, color: 'var(--nj-semantic-color-text-tertiary-default)', flexShrink: 0, marginLeft: 8, transition: 'transform 0.2s', transform: row.isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}
      >
        expand_more
      </span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '0 12px 0 8px', flex: 1, minWidth: 0 }}>
        <DspTypeCell type={row._dsp.type} />
        <span style={{ fontSize: 11, color: '#9e9e9e', fontWeight: 600, letterSpacing: '0.02em', minWidth: 60, flexShrink: 0 }}>{row._dsp.identifiantSNCU}</span>
        <span style={{ fontWeight: 800, fontSize: 15, color: 'var(--nj-semantic-color-text-primary-default)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row._dsp.name}</span>
        <NJBadge variant="information" size="sm" emphasis="subtle">{summary}</NJBadge>
        <span style={{ fontSize: 12, color: 'var(--nj-semantic-color-text-secondary-default)', flexShrink: 0 }}>{row.city}</span>
        {row.territory && <span style={{ fontSize: 11, color: '#9e9e9e', flexShrink: 0 }}>{row.territory}</span>}
        {row._dsp.owner && (
          <span style={{ fontSize: 12, color: 'var(--nj-semantic-color-text-secondary-default)', display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
            <span className="material-symbols-outlined" style={{ fontSize: 14, color: '#9e9e9e' }}>person</span>
            {row._dsp.owner}
          </span>
        )}
        <span style={{ fontSize: 11, color: 'var(--nj-semantic-color-text-tertiary-default)', flexShrink: 0 }}>
          {row.isCollapsed ? 'Déplier' : 'Réduire'}
        </span>
      </div>
    </div>
  );
}

type Level = 'dsp' | 'study' | 'scenario';

interface Props {
  data: ExplorerData;
  selection: Selection | null;
  onNavigate: (s: Selection) => void;
  filteredDspIds?: Set<string> | null;
  filterLabel?: string;
  onClearFilter?: () => void;
  onNetworkScopeChange?: (scope: { dspIds: Set<string> | null; orphanNetworkIds: Set<string> | null }) => void;
}

type DspGroupRow  = { _kind: 'group'; _dsp: DSP; city: string; territory: string; isCollapsed: boolean; studiesCount: number; scenariosCount: number; referenceCount: number };
type DspDataRow   = { _kind: 'data'; id: string; studyType: string; studyName: string; scenarioName: string; scenarioStatus: string; owner: string; modifiedAt: string; _dsp: DSP; _sc: Scenario | null; isOrphan?: boolean; orphanNetwork?: Network };
type DspSpacerRow = { _kind: 'spacer'; id: string };
type DspFlatRow   = DspGroupRow | DspDataRow | DspSpacerRow;
type StudyRow = { id: string; studyType: string; name: string; leader: string; scenarioCount: number; modifiedAt: string; _study: Study };
type ScenarioRow = { id: string; type: string; status: string; name: string; description: string; modifiedAt: string; createdAt: string; _sc: Scenario };

export default function DspListView({ data, selection, onNavigate, filteredDspIds, filterLabel, onClearFilter, onNetworkScopeChange }: Props) {
  const [level, setLevel] = useState<Level>('dsp');
  const [activeDspId, setActiveDspId] = useState<string | null>(null);
  const [activeStudyId, setActiveStudyId] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [studyTypeFilter, setStudyTypeFilter] = useState<string[]>([]);
  const [scenarioStatusFilter, setScenarioStatusFilter] = useState<string[]>([]);
  const [modifiedFrom, setModifiedFrom] = useState('');
  const [modifiedTo, setModifiedTo] = useState('');
  const [collapsedDspIds, setCollapsedDspIds] = useState<Set<string>>(new Set());

  const toggleCollapse = useCallback((dspId: string) => {
    setCollapsedDspIds(prev => {
      const next = new Set(prev);
      if (next.has(dspId)) next.delete(dspId); else next.add(dspId);
      return next;
    });
  }, []);

  const cityById = useMemo(() => Object.fromEntries(data.cities.map(c => [c.id, c])), [data.cities]);
  const territoryById = useMemo(() => Object.fromEntries(data.territories.map(t => [t.id, t])), [data.territories]);
  const activeDsp = useMemo(() => data.dsps.find(d => d.id === activeDspId) ?? null, [data.dsps, activeDspId]);
  const activeStudy = useMemo(() => data.studies.find(s => s.id === activeStudyId) ?? null, [data.studies, activeStudyId]);
  const availableStudyTypes = useMemo(() => Array.from(new Set(data.studies.map(s => s.studyType))).sort(), [data.studies]);

  useEffect(() => {
    setStudyTypeFilter((prev) => prev.filter((type) => availableStudyTypes.includes(type)));
  }, [availableStudyTypes]);

  function drillToScenarios(study: Study) { setActiveStudyId(study.id); setLevel('scenario'); onNavigate({ kind: 'study', id: study.id }); }
  function cloneDsp(dsp: DSP) {
    NJToastService(`Clonage de ${dsp.name}`, { title: 'Clonage', iconName: 'content_copy', dismissAfter: 2500 });
  }
  function openSection(dsp: DSP, section: string) {
    setLevel('dsp'); setActiveDspId(null); setActiveStudyId(null);
    onNavigate({ kind: 'dsp', id: dsp.id });
    NJToastService(`${section} \u2014 ${dsp.name}`, { title: section, iconName: 'open_in_new', dismissAfter: 2500 });
  }
  function deleteDsp(dsp: DSP) {
    NJToastService(`${dsp.name} supprim\u00e9`, { title: 'DSP supprim\u00e9', iconName: 'delete', dismissAfter: 3000 });
  }

  function goToLevel(i: number) {
    if (i === 0) { setLevel('dsp'); setActiveDspId(null); setActiveStudyId(null); }
    else if (i === 1) { setLevel('study'); setActiveStudyId(null); }
  }

  const dspFlatRows = useMemo<DspFlatRow[]>(() => {
    const rows: DspFlatRow[] = [];
    const orphanNetworks = (data.networks ?? []).filter(n => !n.dspId && !n.studyId);

    if (orphanNetworks.length > 0) {
      const latestOrphan = orphanNetworks.reduce(
        (latest, current) => new Date(current.modifiedAt) > new Date(latest.modifiedAt) ? current : latest,
        orphanNetworks[0],
      );
      const orphanDsp: DSP = {
        id: 'dsp-orphan',
        identifiantSNCU: '—',
        name: 'Réseaux sans contrat',
        cityId: orphanNetworks[0].cityId,
        owner: 'Non attribué',
        type: 'hot water',
        status: 'active',
        studyCount: 0,
        scenarioCount: orphanNetworks.length,
        modifiedAt: latestOrphan.modifiedAt,
      };
      rows.push({ _kind: 'group', _dsp: orphanDsp, city: 'Non rattaché', territory: '', studiesCount: 0, scenariosCount: orphanNetworks.length, referenceCount: 0 });
      for (const net of orphanNetworks) {
        rows.push({
          _kind: 'data',
          id: `orphan-${net.id}`,
          studyType: '',
          studyName: '',
          scenarioName: net.name,
          scenarioStatus: 'orphan',
          owner: net.owner,
          modifiedAt: new Date(net.modifiedAt).toLocaleDateString('fr-FR'),
          _dsp: orphanDsp,
          _sc: null,
          isOrphan: true,
          orphanNetwork: net,
        });
      }
    }

    const dspsToShow = (filteredDspIds ? data.dsps.filter(d => filteredDspIds.has(d.id)) : data.dsps)
      .slice()
      .sort((a, b) => a.identifiantSNCU.localeCompare(b.identifiantSNCU, 'fr', { numeric: true, sensitivity: 'base' }));
    for (let i = 0; i < dspsToShow.length; i++) {
      const dsp = dspsToShow[i];
      const city = cityById[dsp.cityId];
      const territory = city ? territoryById[city.territoryId] : null;
      if (rows.length > 0) rows.push({ _kind: 'spacer', id: `spacer-${dsp.id}` });
      const dspStudies = data.studies.filter(s => s.dspId === dsp.id);
      const dspStudyIds = new Set(dspStudies.map(s => s.id));
      const dspScenarios = data.scenarios.filter(sc => dspStudyIds.has(sc.studyId));
      const referencesCount = dspScenarios.filter(sc => sc.status === 'reference').length;
      rows.push({ _kind: 'group', _dsp: dsp, city: city?.name ?? '', territory: territory?.name ?? '', studiesCount: dspStudies.length, scenariosCount: dspScenarios.length, referenceCount: referencesCount });
      if (dspScenarios.length === 0) {
        rows.push({ _kind: 'data', id: `${dsp.id}-empty`, studyType: '', studyName: '', scenarioName: '—', scenarioStatus: 'active', owner: dsp.owner, modifiedAt: new Date(dsp.modifiedAt).toLocaleDateString('fr-FR'), _dsp: dsp, _sc: null! });
      } else {
        for (const sc of dspScenarios) {
          const study = dspStudies.find(s => s.id === sc.studyId);
          rows.push({ _kind: 'data', id: sc.id, studyType: study?.studyType ?? '', studyName: sc.status === 'reference' ? '' : (study?.name ?? ''), scenarioName: sc.name, scenarioStatus: sc.status, owner: dsp.owner, modifiedAt: new Date(sc.modifiedAt).toLocaleDateString('fr-FR'), _dsp: dsp, _sc: sc });
        }
      }
    }

    return rows;
  }, [data.dsps, data.studies, data.scenarios, data.networks, cityById, territoryById, filteredDspIds]);

  const hasStudyTypeFilter = studyTypeFilter.length > 0 && studyTypeFilter.length !== availableStudyTypes.length;
  const hasScenarioStatusFilter = scenarioStatusFilter.length > 0 && scenarioStatusFilter.length !== STATUS_FILTER_OPTIONS.length;

  // Text + filters: always preserve the DSP group header + spacer for any DSP that has ≥1 matching row
  const filteredFlatRows = useMemo<DspFlatRow[]>(() => {
    const hasText = !!searchText.trim();
    const hasFilters = hasStudyTypeFilter || hasScenarioStatusFilter || !!modifiedFrom || !!modifiedTo;
    if (!hasText && !hasFilters) return dspFlatRows;

    const fromDate = modifiedFrom ? new Date(`${modifiedFrom}T00:00:00`) : null;
    const toDate = modifiedTo ? new Date(`${modifiedTo}T23:59:59`) : null;

    const q = searchText.toLowerCase();
    const groups: Array<{ group: DspGroupRow; dataRows: DspDataRow[] }> = [];
    let pendingGroup: DspGroupRow | null = null;
    for (const row of dspFlatRows) {
      if (row._kind === 'spacer') continue;
      if (row._kind === 'group') { pendingGroup = row as DspGroupRow; continue; }
      const dr = row as DspDataRow;
      const searchMatches = !hasText ||
        dr._dsp.name.toLowerCase().includes(q) ||
        dr.studyName.toLowerCase().includes(q) ||
        dr.scenarioName.toLowerCase().includes(q) ||
        dr.owner.toLowerCase().includes(q) ||
        (dr.isOrphan && 'sans contrat'.includes(q)) ||
        (pendingGroup ? groups.find(g => g.group === pendingGroup)?.group.city ?? pendingGroup.city : '').toLowerCase().includes(q);

      const typeMatches = !hasStudyTypeFilter || studyTypeFilter.includes(dr.studyType);
      const statusMatches = !hasScenarioStatusFilter || scenarioStatusFilter.includes(dr.scenarioStatus);
      const modifiedAt = dr._sc
        ? new Date(dr._sc.modifiedAt)
        : (dr.isOrphan && dr.orphanNetwork ? new Date(dr.orphanNetwork.modifiedAt) : null);
      const fromMatches = !fromDate || (modifiedAt !== null && modifiedAt >= fromDate);
      const toMatches = !toDate || (modifiedAt !== null && modifiedAt <= toDate);
      const matches = searchMatches && typeMatches && statusMatches && fromMatches && toMatches;

      if (matches && pendingGroup) {
        let grp = groups.find(g => g.group === pendingGroup);
        if (!grp) { grp = { group: pendingGroup, dataRows: [] }; groups.push(grp); }
        grp.dataRows.push(dr);
      }
    }
    const result: DspFlatRow[] = [];
    groups.forEach((g, i) => {
      if (i > 0) result.push({ _kind: 'spacer', id: `spacer-${g.group._dsp.id}` });
      result.push(g.group);
      g.dataRows.forEach(r => result.push(r));
    });
    return result;
  }, [dspFlatRows, searchText, hasStudyTypeFilter, hasScenarioStatusFilter, studyTypeFilter, scenarioStatusFilter, modifiedFrom, modifiedTo]);

  const studyTypeSelectedText = useMemo(() => {
    const selectedCount = studyTypeFilter.length;
    const totalCount = availableStudyTypes.length;
    if (selectedCount === 0 || totalCount === 0 || selectedCount === totalCount) return 'Tous';
    return `${selectedCount} sélectionné${selectedCount > 1 ? 's' : ''}`;
  }, [studyTypeFilter, availableStudyTypes]);

  const scenarioStatusSelectedText = useMemo(() => {
    const selectedCount = scenarioStatusFilter.length;
    const totalCount = STATUS_FILTER_OPTIONS.length;
    if (selectedCount === 0 || selectedCount === totalCount) return 'Tous';
    return `${selectedCount} sélectionné${selectedCount > 1 ? 's' : ''}`;
  }, [scenarioStatusFilter]);

  const isTableFilterActive = !!searchText.trim() || hasStudyTypeFilter || hasScenarioStatusFilter || !!modifiedFrom || !!modifiedTo;

  useEffect(() => {
    if (!onNetworkScopeChange) return;
    if (!isTableFilterActive) {
      onNetworkScopeChange({ dspIds: null, orphanNetworkIds: null });
      return;
    }

    const groupIds = new Set(
      filteredFlatRows
        .filter((row): row is DspGroupRow => row._kind === 'group')
        .map((row) => row._dsp.id)
        .filter((id) => id !== 'dsp-orphan'),
    );
    const orphanNetworkIds = new Set(
      filteredFlatRows
        .filter((row): row is DspDataRow => row._kind === 'data' && !!row.isOrphan && !!row.orphanNetwork)
        .map((row) => row.orphanNetwork!.id),
    );
    onNetworkScopeChange({ dspIds: groupIds, orphanNetworkIds });
  }, [onNetworkScopeChange, isTableFilterActive, filteredFlatRows]);

  const visibleGroupIds = useMemo(
    () => filteredFlatRows.filter(r => r._kind === 'group').map(r => (r as DspGroupRow)._dsp.id),
    [filteredFlatRows],
  );

  const allVisibleCollapsed = useMemo(
    () => visibleGroupIds.length > 0 && visibleGroupIds.every(id => collapsedDspIds.has(id)),
    [visibleGroupIds, collapsedDspIds],
  );

  const toggleAllGroups = useCallback(() => {
    if (allVisibleCollapsed) {
      setCollapsedDspIds(new Set());
      return;
    }
    setCollapsedDspIds(new Set(visibleGroupIds));
  }, [allVisibleCollapsed, visibleGroupIds]);

  // Accordion collapse: filter data rows for collapsed DSPs and inject isCollapsed flag into group rows
  const visibleFlatRows = useMemo<DspFlatRow[]>(() => {
    const result: DspFlatRow[] = [];
    let collapsed = false;
    for (const row of filteredFlatRows) {
      if (row._kind === 'group') {
        collapsed = collapsedDspIds.has(row._dsp.id);
        result.push({ ...row, isCollapsed: collapsed } as DspGroupRow);
      } else if (row._kind === 'spacer') {
        result.push(row);
      } else {
        if (!collapsed) result.push(row);
      }
    }
    return result;
  }, [filteredFlatRows, collapsedDspIds]);

  const dspDataColumns = useMemo<ColDef<DspDataRow>[]>(() => [
    { field: 'studyType', headerName: 'Type étude', width: 155, cellRenderer: ({ value, data: row }: { value: string; data: DspDataRow }) => (
      <div style={{ display: 'inline-flex', alignItems: 'center', height: '100%' }}>
        {row.isOrphan
          ? <NJBadge variant="warning" size="sm" emphasis="subtle">Sans contrat</NJBadge>
          : row.scenarioStatus === 'reference'
          ? <NJBadge variant="success" size="sm" emphasis="subtle">Référence</NJBadge>
          : value && <NJBadge variant={STUDY_TYPE_VARIANT[value as StudyType] ?? 'information'} size="sm" emphasis="subtle">{value}</NJBadge>}
      </div>
    )},
    { field: 'studyName', headerName: 'Nom étude', flex: 2, minWidth: 160, cellRenderer: ({ value, data: row }: { value: string; data: DspDataRow }) => (
      <div style={{ display: 'flex', alignItems: 'center', height: '100%', fontSize: 13, color: 'var(--nj-semantic-color-text-primary-default)' }}>
        {row.isOrphan ? '—' : value || '—'}
      </div>
    )},
    { field: 'scenarioName', headerName: 'Nom réseau', flex: 2, minWidth: 180, cellRenderer: ({ value, data: row }: { value: string; data: DspDataRow }) => (
      <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
        {row._sc ? (
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {row.scenarioStatus === 'archived' && (
              <NJTooltip text="Réseau archivé" placement="top" hasArrow>
                <span className="material-symbols-outlined" style={{ fontSize: 14, color: SC_COLOR.archived }}>inventory_2</span>
              </NJTooltip>
            )}
            <NJTooltip text={row._sc.description || 'Aucune description'} placement="top" hasArrow>
              <button
                onClick={() => onNavigate({ kind: 'scenario', id: row._sc!.id })}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 13, fontWeight: 600, color: 'var(--nj-semantic-color-text-primary-default)', fontFamily: 'inherit', textAlign: 'left', fontStyle: row.scenarioStatus === 'archived' ? 'italic' : 'normal' }}
                onMouseEnter={e => (e.currentTarget.style.color = 'var(--nj-semantic-color-text-brand-primary-default)')}
                onMouseLeave={e => (e.currentTarget.style.color = 'var(--nj-semantic-color-text-primary-default)')}
              >
                {value}
              </button>
            </NJTooltip>
          </div>
        ) : (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: row.isOrphan ? '#a16207' : 'var(--nj-semantic-color-text-tertiary-default)' }}>
            {row.isOrphan && <span className="material-symbols-outlined" style={{ fontSize: 14 }}>report</span>}
            {value}
          </span>
        )}
      </div>
    )},
    { field: 'owner', headerName: 'Propriétaire', width: 150, cellRenderer: ({ value }: { value: string }) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, height: '100%', fontSize: 13 }}>
        <span className="material-symbols-outlined" style={{ fontSize: 15, color: '#9e9e9e', flexShrink: 0 }}>person</span>
        <span style={{ color: 'var(--nj-semantic-color-text-secondary-default)' }}>{value}</span>
      </div>
    )},
    { field: 'modifiedAt', headerName: 'Modifié le', width: 110 },
    { headerName: '', width: 56, maxWidth: 56, sortable: false, resizable: false, cellRenderer: ({ data: row }: { data: DspDataRow }) => (
      <KebabMenuCell
        dsp={row._dsp}
        onClone={cloneDsp}
        onOpenSection={openSection}
        onDelete={deleteDsp}
      />
    )},
  ], [onNavigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const studyRows = useMemo<StudyRow[]>(() => {
    const studies = activeDspId ? data.studies.filter(s => s.dspId === activeDspId) : [];
    return studies.map(s => ({ id: s.id, studyType: s.studyType, name: s.name, leader: s.leader, scenarioCount: s.scenarioCount, modifiedAt: new Date(s.modifiedAt).toLocaleDateString('fr-FR'), _study: s }));
  }, [data.studies, activeDspId]);

  const studyColumns = useMemo<ColDef<StudyRow>[]>(() => [
    { field: 'studyType', headerName: 'Type', width: 150, cellRenderer: ({ value }: { value: string }) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, height: '100%' }}>
        <span className="material-symbols-outlined" style={{ fontSize: 15, color: STUDY_TYPE_COLOR[value] ?? '#666' }}>folder</span>
        <NJBadge variant={STUDY_TYPE_VARIANT[value as StudyType] ?? 'information'} size="sm" emphasis="subtle">{value}</NJBadge>
      </div>
    )},
    { field: 'name', headerName: 'Nom \u00e9tude', flex: 2, minWidth: 160, cellRenderer: ({ value, data: row }: { value: string; data: StudyRow }) => (
      <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
        <button onClick={() => onNavigate({ kind: 'study', id: row.id })} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 600, fontSize: 13, color: 'var(--nj-semantic-color-text-primary-default)', fontFamily: 'inherit', textAlign: 'left' }} onMouseEnter={e => (e.currentTarget.style.color = 'var(--nj-semantic-color-text-brand-primary-default)')} onMouseLeave={e => (e.currentTarget.style.color = 'var(--nj-semantic-color-text-primary-default)')}>{value}</button>
      </div>
    )},
    { field: 'leader', headerName: 'Responsable', flex: 1, minWidth: 130 },
    { field: 'scenarioCount', headerName: 'R\u00e9seaux', width: 90, type: 'numericColumn' },
    { headerName: 'Noms des r\u00e9seaux', flex: 2, minWidth: 180, sortable: false, cellRenderer: ({ data: row }: { data: StudyRow }) => {
      const names = data.scenarios.filter(s => s.studyId === row.id).map(s => s.name);
      return (
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4, height: '100%', padding: '4px 0' }}>
          {names.slice(0, 3).map((n, i) => (
            <span key={i} style={{ fontSize: 11, background: '#e8f5e9', color: '#2e7d32', borderRadius: 10, padding: '2px 8px', whiteSpace: 'nowrap', cursor: 'pointer' }}
              onClick={() => { const sc = data.scenarios.find(s => s.studyId === row.id && s.name === n); if (sc) onNavigate({ kind: 'scenario', id: sc.id }); }}>{n}</span>
          ))}
          {names.length > 3 && <span style={{ fontSize: 11, color: 'var(--nj-semantic-color-text-tertiary-default)' }}>+{names.length - 3}</span>}
        </div>
      );
    }},
    { field: 'modifiedAt', headerName: 'Modifi\u00e9 le', width: 110 },
    { headerName: '', width: 140, maxWidth: 140, sortable: false, resizable: false, cellRenderer: ({ data: row }: { data: StudyRow }) => <DrillBtn label="R\u00e9seaux" onClick={e => { e.stopPropagation(); drillToScenarios(row._study); }} /> },
  ], [onNavigate, data.scenarios]); // eslint-disable-line react-hooks/exhaustive-deps

  const scenarioRows = useMemo<ScenarioRow[]>(() => {
    const scenarios = activeStudyId ? data.scenarios.filter(s => s.studyId === activeStudyId) : [];
    return scenarios.map(s => ({ id: s.id, type: s.type, status: s.status, name: s.name, description: s.description ?? '', modifiedAt: new Date(s.modifiedAt).toLocaleDateString('fr-FR'), createdAt: new Date(s.createdAt).toLocaleDateString('fr-FR'), _sc: s }));
  }, [data.scenarios, activeStudyId]);

  const scenarioColumns = useMemo<ColDef<ScenarioRow>[]>(() => [
    { field: 'status', headerName: 'Statut', width: 130, cellRenderer: ({ value }: { value: string }) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, height: '100%' }}>
        <span className="material-symbols-outlined" style={{ fontSize: 15, color: SC_COLOR[value] ?? '#666' }}>{SC_ICON[value] ?? 'science'}</span>
        <NJBadge variant={SC_STATUS_VARIANT[value as keyof typeof SC_STATUS_VARIANT] ?? 'information'} size="sm" emphasis="subtle">{SC_STATUS_LABEL[value] ?? value}</NJBadge>
      </div>
    )},
    { field: 'name', headerName: 'Nom r\u00e9seau', flex: 2, minWidth: 160, cellRenderer: ({ value, data: row }: { value: string; data: ScenarioRow }) => (
      <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
        <NJTooltip text={row.description || 'Aucune description'} placement="top" hasArrow>
          <button onClick={() => onNavigate({ kind: 'scenario', id: row.id })} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 600, fontSize: 13, color: 'var(--nj-semantic-color-text-primary-default)', fontFamily: 'inherit', textAlign: 'left' }} onMouseEnter={e => (e.currentTarget.style.color = 'var(--nj-semantic-color-text-brand-primary-default)')} onMouseLeave={e => (e.currentTarget.style.color = 'var(--nj-semantic-color-text-primary-default)')}>{value}</button>
        </NJTooltip>
      </div>
    )},
    { field: 'type', headerName: 'Type r\u00e9seau', width: 130, valueFormatter: ({ value }) => TYPE_LABEL[value] ?? value },
    { field: 'description', headerName: 'Description', flex: 2, minWidth: 160 },
    { field: 'createdAt', headerName: 'Cr\u00e9\u00e9 le', width: 110 },
    { field: 'modifiedAt', headerName: 'Modifi\u00e9 le', width: 110 },
    { headerName: 'Vue r\u00e9seau', width: 130, maxWidth: 130, sortable: false, resizable: false, cellRenderer: ({ data: row }: { data: ScenarioRow }) => (
      <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
        <button
          onClick={() => onNavigate({ kind: 'scenario', id: row.id })}
          style={{ fontSize: 12, fontWeight: 600, color: '#2e7d32', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px', borderRadius: 4, display: 'inline-flex', alignItems: 'center', gap: 4, fontFamily: 'inherit' }}
          onMouseEnter={e => (e.currentTarget.style.background = '#e8f5e9')}
          onMouseLeave={e => (e.currentTarget.style.background = 'none')}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 14 }}>open_in_new</span>
          Ouvrir
        </button>
      </div>
    )},
  ], [onNavigate]);

  const breadcrumbItems = level === 'dsp' ? ['Tous les contrats']
    : level === 'study' ? ['Tous les contrats', activeDsp?.name ?? '']
    : ['Tous les contrats', activeDsp?.name ?? '', activeStudy?.name ?? ''];

  const visibleDspCount = searchText.trim()
    ? filteredFlatRows.filter(r => r._kind === 'group').length
    : (filteredDspIds ? filteredDspIds.size : data.dsps.length);
  const countLabel = level === 'dsp' ? `${visibleDspCount} contrat${visibleDspCount > 1 ? 's' : ''}`
    : level === 'study' ? `${studyRows.length} \u00e9tude${studyRows.length > 1 ? 's' : ''}`
    : `${scenarioRows.length} r\u00e9seau${scenarioRows.length > 1 ? 'x' : ''}`;
  const levelIcon = level === 'dsp' ? 'hub' : level === 'study' ? 'folder' : 'science';

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: '16px 24px', gap: 12, background: 'var(--nj-semantic-color-background-neutral-secondary-default)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0, minHeight: 32 }}>
        <Breadcrumb items={breadcrumbItems} onClickIdx={goToLevel} />
        <div style={{ flex: 1 }} />
        {level === 'dsp' && (
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <span className="material-symbols-outlined" style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', fontSize: 15, color: 'var(--nj-semantic-color-text-tertiary-default)', pointerEvents: 'none' }}>search</span>
            <input
              type="text"
              placeholder="Rechercher contrat, étude, réseau…"
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              style={{ paddingLeft: 28, paddingRight: searchText ? 26 : 10, paddingTop: 4, paddingBottom: 4, border: '1px solid var(--nj-semantic-color-border-neutral-secondary-default)', borderRadius: 20, fontSize: 12, width: 240, fontFamily: 'inherit', background: 'var(--nj-semantic-color-background-neutral-primary-default)', color: 'var(--nj-semantic-color-text-primary-default)', outline: 'none' }}
            />
            {searchText && (
              <button onClick={() => setSearchText('')} style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'inline-flex', color: 'var(--nj-semantic-color-text-tertiary-default)' }}>
                <span className="material-symbols-outlined" style={{ fontSize: 13 }}>close</span>
              </button>
            )}
          </div>
        )}
        {level === 'dsp' && (
          <button
            onClick={toggleAllGroups}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: 'var(--nj-semantic-color-text-secondary-default)', background: 'var(--nj-semantic-color-background-neutral-primary-default)', border: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)', borderRadius: 4, padding: '5px 10px', cursor: 'pointer', fontFamily: 'inherit' }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 14 }}>{allVisibleCollapsed ? 'unfold_more' : 'unfold_less'}</span>
            {allVisibleCollapsed ? 'Tout déplier' : 'Tout réduire'}
          </button>
        )}
        <NJBadge variant="information" size="sm" emphasis="subtle">{countLabel}</NJBadge>
        {level === 'dsp' && (
          <button
            onClick={() => onNavigate({ kind: 'new-dsp', id: 'new' })}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: 'var(--nj-semantic-color-text-brand-primary-default)', background: 'none', border: '1px solid var(--nj-semantic-color-border-brand-primary-default)', borderRadius: 4, padding: '5px 12px', cursor: 'pointer', fontFamily: 'inherit' }}
            onMouseEnter={e => (e.currentTarget.style.background = 'var(--nj-semantic-color-background-brand-secondary-default)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'none')}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>add_circle</span>
            Ajouter un contrat
          </button>
        )}
      </div>

      {filterLabel && onClearFilter && level === 'dsp' && (
        <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12, color: 'var(--nj-semantic-color-text-tertiary-default)' }}>Filtré par :</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, background: 'var(--nj-semantic-color-background-brand-secondary-default)', color: 'var(--nj-semantic-color-text-brand-primary-default)', border: '1px solid var(--nj-semantic-color-border-brand-primary-default)', borderRadius: 20, padding: '2px 10px' }}>
            <span className="material-symbols-outlined" style={{ fontSize: 13 }}>filter_alt</span>
            {filterLabel}
            <button onClick={onClearFilter} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginLeft: 2, display: 'inline-flex', color: 'inherit' }}>
              <span className="material-symbols-outlined" style={{ fontSize: 13 }}>close</span>
            </button>
          </span>
        </div>
      )}
      {level === 'dsp' && (
        <div style={{ flexShrink: 0, display: 'flex', alignItems: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: 'var(--nj-semantic-color-text-tertiary-default)', marginBottom: 8 }}>Filtres :</span>
          <div style={{ width: 220, minWidth: 220, flex: '0 0 220px' }}>
            <NJMultiSelect.Root
              id="dlv-study-type-filter"
              label="Type étude"
              listNavigationLabel="Utiliser les flèches et Entrée pour cocher"
              buttonDefaultValueLabel="Tous"
              displaySelectedItems={false}
              selectedText={studyTypeSelectedText}
              value={studyTypeFilter}
              onChange={(values) => setStudyTypeFilter(values)}
            >
              {availableStudyTypes.map(type => <NJMultiSelect.Item key={type} value={type}>{type}</NJMultiSelect.Item>)}
            </NJMultiSelect.Root>
          </div>
          <div style={{ width: 220, minWidth: 220, flex: '0 0 220px' }}>
            <NJMultiSelect.Root
              id="dlv-scenario-status-filter"
              label="Statut réseau"
              listNavigationLabel="Utiliser les flèches et Entrée pour cocher"
              buttonDefaultValueLabel="Tous"
              displaySelectedItems={false}
              selectedText={scenarioStatusSelectedText}
              value={scenarioStatusFilter}
              onChange={(values) => setScenarioStatusFilter(values)}
            >
              <NJMultiSelect.Item value="active">Actif</NJMultiSelect.Item>
              <NJMultiSelect.Item value="archived">Archivé</NJMultiSelect.Item>
              <NJMultiSelect.Item value="reference">Référence</NJMultiSelect.Item>
              <NJMultiSelect.Item value="orphan">Sans contrat</NJMultiSelect.Item>
            </NJMultiSelect.Root>
          </div>
          <div style={{ width: 148, minWidth: 148, flex: '0 0 148px' }}>
            <NJTooltip text="Afficher les lignes modifiées à partir de cette date (inclus)." placement="top" hasArrow>
              <NJFormItem
                id="dlv-modified-from"
                label="Après"
                type="date"
                value={modifiedFrom}
                onChange={(e) => setModifiedFrom((e.target as HTMLInputElement).value)}
                labelKind="floating"
              />
            </NJTooltip>
          </div>
          <div style={{ width: 148, minWidth: 148, flex: '0 0 148px' }}>
            <NJTooltip text="Afficher les lignes modifiées jusqu'à cette date (inclus)." placement="top" hasArrow>
              <NJFormItem
                id="dlv-modified-to"
                label="Avant"
                type="date"
                value={modifiedTo}
                onChange={(e) => setModifiedTo((e.target as HTMLInputElement).value)}
                labelKind="floating"
              />
            </NJTooltip>
          </div>
          {(hasStudyTypeFilter || hasScenarioStatusFilter || modifiedFrom || modifiedTo) && (
            <button
              onClick={() => {
                setStudyTypeFilter([]);
                setScenarioStatusFilter([]);
                setModifiedFrom('');
                setModifiedTo('');
              }}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--nj-semantic-color-text-secondary-default)', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', fontFamily: 'inherit', marginBottom: 8 }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 14 }}>filter_alt_off</span>
              Réinitialiser
            </button>
          )}
        </div>
      )}
      <style>{`
        [data-testid="study-list"],
        [data-testid="scenario-list"] { height: 100% !important; display: flex; flex-direction: column; }
        [data-testid="study-list"] > div,
        [data-testid="scenario-list"] > div { flex: 1; min-height: 0; }
        .network-list-grid .ag-header-cell[col-id="modifiedAt"],
        .network-list-grid .ag-cell[col-id="modifiedAt"] {
          border-right: none !important;
        }
      `}</style>
      <div style={{ flex: 1, overflow: 'hidden', background: 'var(--nj-semantic-color-background-neutral-primary-default)', borderRadius: 6, border: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)', display: 'flex', flexDirection: 'column' }}>
        {level === 'dsp' && (
          <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <AgGridReact<DspFlatRow>
              rowData={visibleFlatRows}
              columnDefs={dspDataColumns as ColDef<DspFlatRow>[]}
              isFullWidthRow={(p) => { const k = (p.rowNode.data as DspFlatRow)?._kind; return k === 'group' || k === 'spacer'; }}
              fullWidthCellRenderer={(p: { data?: DspFlatRow }) => (p.data as DspFlatRow)?._kind === 'spacer' ? <div style={{ height: '100%', background: 'var(--nj-semantic-color-background-neutral-secondary-default)' }} /> : <DspGroupRenderer data={p.data} onToggle={toggleCollapse} />}
              getRowHeight={(p) => { const k = (p.data as DspFlatRow)?._kind; return k === 'group' ? 46 : k === 'spacer' ? 10 : 42; }}
              getRowId={(p) => { const r = p.data as DspFlatRow; return r._kind === 'group' ? `grp-${(r as DspGroupRow)._dsp.id}` : r._kind === 'spacer' ? (r as DspSpacerRow).id : (r as DspDataRow).id; }}
              getRowStyle={(p) => {
                if ((p.data as DspFlatRow)?._kind !== 'data') return {};
                const row = p.data as DspDataRow;
                const typeColor = TYPE_COLOR[row._dsp.type] ?? '#9e9e9e';
                const borderLeft = `4px solid ${typeColor}55`;
                if (row.isOrphan) return { background: '#fff8e1', borderLeft: '4px solid #f59e0b' };
                if (row.scenarioStatus === 'archived') return { background: '#f5f5f5', color: '#9e9e9e', borderLeft };
                if (row.scenarioStatus === 'reference') return { background: '#ffffff', borderLeft };
                return { background: '#ffffff', borderLeft };
              }}
              suppressMovableColumns
              enableBrowserTooltips
              pagination
              paginationPageSize={200}
              paginationPageSizeSelector={[200, 500, 1000]}
              className="ag-theme-quartz network-list-grid mt-4"
              domLayout="normal"
              style={{ height: '100%' }}
            />
          </div>
        )}
        {level === 'study' && <DataTable<StudyRow> key="study" header={studyColumns} data={studyRows} testId="study-list" isPaginated verticalScroll />}
        {level === 'scenario' && <DataTable<ScenarioRow> key="scenario" header={scenarioColumns} data={scenarioRows} testId="scenario-list" isPaginated verticalScroll />}
      </div>

      {level === 'scenario' && selection?.kind === 'scenario' && (
        <div style={{ flexShrink: 0, padding: '8px 14px', background: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#2e7d32' }}>
          <span className="material-symbols-outlined" style={{ fontSize: 16 }}>info</span>
          <NJText as="span" scale="sm" style={{ color: '#2e7d32' }}>Sc\u00e9nario s\u00e9lectionn\u00e9 \u2014 consultez le panneau de d\u00e9tail pour ouvrir dans la vue r\u00e9seau.</NJText>
        </div>
      )}
    </div>
  );
}
